import type { ArtMetadata, ArtTextStyle } from "../schedule-presets/art-metadata";

const loads = new Map<string, Promise<boolean>>();
const available = new Set<string>();
const googleLoads = new Map<string, Promise<boolean>>();
type FontStyle = Pick<ArtTextStyle, "font" | "weight">;
const fontKey = (style: FontStyle) => `${style.weight ?? 400} 28px ${JSON.stringify(style.font)}`;

export function prepareGoogleArtFont(font: string, weights: number[]) {
  const existing = googleLoads.get(font);
  if (existing) return existing;
  const query = new URLSearchParams({ family: `${font}:wght@${weights.join(";")}`, display: "swap" });
  const loading = new Promise<boolean>(resolve => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${query}`;
    link.onload = () => resolve(true);
    link.onerror = () => { link.remove(); resolve(false); };
    document.head.appendChild(link);
  });
  googleLoads.set(font, loading);
  loading.then(loaded => { if (!loaded) googleLoads.delete(font); });
  return loading;
}

export function loadArtFont(style: FontStyle): Promise<boolean> {
  const key = fontKey(style);
  if (!loads.has(key)) {
    loads.set(key, (async () => {
      try {
        const stylesheet = googleLoads.get(style.font);
        if (stylesheet && !await stylesheet) return false;
        await document.fonts.ready;
        const faces = await document.fonts.load(key, "Schedule 0123456789 直播行程");
        if (!faces.length) return false;
        available.add(key);
        return true;
      } catch {
        return false;
      }
    })());
    const loading = loads.get(key)!;
    loading.then(loaded => {
      // A failed lookup must be retried after a stylesheet or local face becomes available.
      if (!loaded && loads.get(key) === loading) loads.delete(key);
    });
  }
  return loads.get(key)!;
}

export function artFontStyle(style: FontStyle) {
  return {
    fontFamily: available.has(fontKey(style)) ? style.font : "sans-serif",
    fontStyle: String(style.weight ?? 400),
  };
}

export function artFontWeight(fontStyle: string): number {
  const style = fontStyle.toLowerCase();
  if (style.includes("bold")) return 700;
  const numeric = Number.parseInt(style, 10);
  return Number.isFinite(numeric) ? numeric : 400;
}

export async function loadArtFonts(metadata: ArtMetadata | null | undefined) {
  await Promise.all(Object.values(metadata?.styles ?? {}).map(loadArtFont));
}
